// Catalog.ID Client — Organization Recovery Kit helpers
// ============================================================================
// The org workspace's Recovery Kit pane runs under a DELEGATE session: the
// organization account has no session of its own (its password is a
// never-disclosed throwaway), so there is no identity to switch to and every
// step here has to name the ORGANIZATION's cid explicitly. Personal-scoped
// calls (/api/account/keybag, /api/account/recoverykit) would silently operate
// on the delegate instead.
//
// Kept free of DOM so the wiring above it — which cid is read, which supersecret
// is verified, what the drill reports — is unit-testable.

import * as accountApi from '../api/account';
import * as organizationApi from '../api/organization';
import type { KeyBagResponse, PublicKeyInfo } from '../api/types';
import { decryptKeyBag, type KeyBag } from '../crypto/keybag';
import { reconstructSupersecret } from '../crypto/recovery-kit';
import type { RecoveryCardPayload } from '../crypto/recovery-card';
import type { RecoveryKitSource } from './recovery-kit-creator';

export const ORG_SUPERSECRET_REQUIRED = 'Enter the organization supersecret';

export const ORG_KEYBAG_DECRYPT_FAILED =
    'Invalid organization supersecret — could not decrypt the organization key bag';

export const ORG_DRILL_DECRYPT_FAILED =
    'The supersecret reconstructed from these sheets does not decrypt the organization key bag — '
    + 'the sheets belong to a different organization or to a superseded supersecret';

/** Seams for tests; production wiring is the default. */
export interface OrgRecoveryDeps {
    getOrgKeyBag(orgCid: string): Promise<KeyBagResponse>;
    decryptKeyBag(response: KeyBagResponse, supersecret: string): Promise<KeyBag>;
    getPublicKeys(cid: string): Promise<PublicKeyInfo[]>;
    reconstructSupersecret(cards: RecoveryCardPayload[]): string;
    markTested(orgCid: string): Promise<void>;
}

function withDefaults(over: Partial<OrgRecoveryDeps>): OrgRecoveryDeps {
    return {
        getOrgKeyBag: organizationApi.getOrgKeyBag,
        decryptKeyBag,
        getPublicKeys: accountApi.getPublicKeys,
        reconstructSupersecret,
        markTested: organizationApi.markOrgRecoveryKitTested,
        ...over,
    };
}

/**
 * Kit material source for `createRecoveryKitCreator`, scoped to an organization:
 * the supersecret typed in is verified by decrypting the ORG key bag, and the
 * bundle is built against the ORG's published public keys.
 */
export function buildOrgKitSource(orgCid: string, over: Partial<OrgRecoveryDeps> = {}): RecoveryKitSource {
    const deps = withDefaults(over);
    return {
        needsSupersecretInput: true,
        resolve: async (supersecretInput) => {
            if (!supersecretInput) throw new Error(ORG_SUPERSECRET_REQUIRED);
            const kbResp = await deps.getOrgKeyBag(orgCid);
            let bag: KeyBag;
            try {
                bag = await deps.decryptKeyBag(kbResp, supersecretInput);
            } catch {
                throw new Error(ORG_KEYBAG_DECRYPT_FAILED);
            }
            const publicKeys = await deps.getPublicKeys(orgCid);
            return { supersecret: supersecretInput, bag, publicKeys };
        },
    };
}

/**
 * Org fire drill (Recovery Kit Addendum §14) without a backup file. A personal
 * drill decrypts an exported bundle the holder brings along; an organization
 * has none — the wizard prints sheets only — so the quorum is tested against the
 * organization's key bag held on this node, which is the same ciphertext the
 * supersecret has to open on a real recovery.
 *
 * Resolves with the user-facing PASSED sentence; throws with a user-facing
 * message on failure.
 */
export async function runOrgRecoveryDrill(
    orgCid: string,
    cards: RecoveryCardPayload[],
    over: Partial<OrgRecoveryDeps> = {},
): Promise<string> {
    const deps = withDefaults(over);

    // Sheet-set consistency and the below-quorum guard live here — surface them
    // verbatim, they tell the custodian exactly which sheet to fetch next.
    const supersecret = deps.reconstructSupersecret(cards);

    const kbResp = await deps.getOrgKeyBag(orgCid);
    let bag: KeyBag;
    try {
        bag = await deps.decryptKeyBag(kbResp, supersecret);
    } catch {
        throw new Error(ORG_DRILL_DECRYPT_FAILED);
    }

    try {
        await deps.markTested(orgCid);
    } catch { /* the drill result stands even if the timestamp write fails */ }

    return `Recovery test PASSED — organization supersecret reconstructed from ${cards.length} sheets `
        + `and all ${bag.entries.length} key-bag entries decrypted. Recorded as tested today.`;
}
